/**
 * Azure Application Insights Service
 *
 * Provides client-side telemetry for the Vue 3 SPA:
 * - Page views (integrated with Vue Router)
 * - User sessions
 * - Exceptions and errors
 * - API call tracking (automatic via fetch/XHR instrumentation)
 * - Custom events
 */

import { ApplicationInsights } from '@microsoft/applicationinsights-web'
import type { Router } from 'vue-router'

let appInsights: ApplicationInsights | null = null

/**
 * Initialize Application Insights with Vue Router integration
 */
export function initializeAppInsights(router: Router): ApplicationInsights | null {
    const connectionString = import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING

    if (!connectionString) {
        console.log('[AppInsights] No connection string configured - telemetry disabled')
        return null
    }

    appInsights = new ApplicationInsights({
        config: {
            connectionString,
            enableAutoRouteTracking: false, // We'll handle this manually for better control
            enableCorsCorrelation: false, // Disabled to avoid CORS issues with Request-Id header
            enableRequestHeaderTracking: false, // Disabled to avoid CORS preflight failures
            enableResponseHeaderTracking: false,
            disableFetchTracking: false,
            disableAjaxTracking: false,
            autoTrackPageVisitTime: true,
        }
    })

    appInsights.loadAppInsights()

    // Set cloud role name for Application Map
    appInsights.addTelemetryInitializer((envelope) => {
        if (envelope.tags) {
            envelope.tags['ai.cloud.role'] = 'Device Management UI'
        }
    })

    // Track page views on route changes
    router.afterEach((to, from) => {
        if (appInsights) {
            appInsights.trackPageView({
                name: to.meta.title as string || to.name as string || to.path,
                uri: to.fullPath,
                refUri: from.fullPath,
                properties: {
                    routeName: to.name as string,
                    fromRoute: from.name as string,
                }
            })
        }
    })

    console.log('[AppInsights] Telemetry initialized')
    return appInsights
}

/**
 * Set authenticated user context
 * Call this after user logs in to associate telemetry with user
 */
export function setAuthenticatedUser(userId: string, accountId?: string): void {
    if (appInsights) {
        appInsights.setAuthenticatedUserContext(userId, accountId, true)
        console.log('[AppInsights] User context set')
    }
}

/**
 * Clear user context on logout
 */
export function clearAuthenticatedUser(): void {
    if (appInsights) {
        appInsights.clearAuthenticatedUserContext()
        console.log('[AppInsights] User context cleared')
    }
}

/**
 * Track a custom event
 */
export function trackEvent(name: string, properties?: Record<string, string>): void {
    if (appInsights) {
        appInsights.trackEvent({ name }, properties)
    }
}

/**
 * Track an exception
 */
export function trackException(error: Error, properties?: Record<string, string>): void {
    if (appInsights) {
        appInsights.trackException({ exception: error }, properties)
    }
}

/**
 * Get the Application Insights instance (for advanced usage)
 */
export function getAppInsights(): ApplicationInsights | null {
    return appInsights
}
